/**
 * LE CHOIX D'UNE SOURCE — les familles (sous-familles, marques) qu'une tuile peut déverser, chacune avec son compte de
 * déclinaisons vendables, et une recherche : le menu de l'inspecteur, et la fenêtre « Choisir » qui s'ouvre quand on en
 * AJOUTE une (maquette `srcPop`, `srcModal`).
 *
 * ⚠️ ON CHOISIT DANS LE RÉFÉRENTIEL, ON NE L'ENRICHIT PAS D'ICI : créer une famille reste le travail des Référentiels.
 *    Une valeur sans article vendable n'est pas proposée — elle donnerait une tuile qui ouvre un niveau vide.
 * ⚠️ LA LISTE SE LIT À L'OUVERTURE, ET UN ÉCHEC SE DIT AVEC SON MOTIF : une liste vide affichée sur un échec ferait
 *    croire que le magasin n'a aucune famille.
 */
import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import { Icon, ModalBackdrop, useOverlayPanel } from '@orgazone/ui-web';
import type { QuickSourceKind } from '@orgazone/sync-contract';
import { api, type StudioSource } from '../api';
import { t } from '../i18n';
import { sourceWord } from './quick-studio-model';

/** Lignes dessinées au plus — au-delà, la liste dit qu'elle n'en montre que les premières, et la recherche affine. */
const SHOWN_MAX = 200;

type ListState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; rows: StudioSource[] };

/** Une recherche qui ignore la casse et les accents — « eté » trouve « Été ». */
const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/**
 * Les deux formes de la liste, aux mesures de la maquette : resserrée dans le menu de l'inspecteur, plus aérée dans la
 * fenêtre — la recherche dans un bandeau, la liste qui défile dessous.
 */
const LOOK = {
  menu: {
    head: { padding: 7, borderBottom: '1px solid var(--ink-100)' },
    input: { height: 32, padding: '0 10px', background: 'var(--ink-50)', fontSize: 'var(--t-body)' },
    list: { maxHeight: 240, overflowY: 'auto', padding: 5 },
    row: { gap: 8, padding: '7px 9px', fontSize: 'var(--t-body)' },
    count: 'var(--t-meta)', check: 13,
  },
  window: {
    head: { padding: '10px 18px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)' },
    input: { height: 34, padding: '0 11px', background: 'var(--paper)', fontSize: 'var(--t-lead)' },
    list: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 10px' },
    row: { gap: 10, padding: '10px 10px', fontSize: 'var(--t-title)' },
    count: 'var(--t-note)', check: 14,
  },
} satisfies Record<string, { head: CSSProperties; input: CSSProperties; list: CSSProperties; row: CSSProperties; count: string; check: number }>;

export function QuickStudioSourceList({ kind, current, onPick, variant = 'menu' }: {
  kind: QuickSourceKind;
  /** La source que la tuile déverse déjà — cochée dans la liste. */
  current: string | null;
  onPick: (source: StudioSource) => void;
  /** Le menu de l'inspecteur, ou la fenêtre d'ajout. */
  variant?: keyof typeof LOOK;
}) {
  const [state, setState] = useState<ListState>({ status: 'loading' });
  const [query, setQuery] = useState('');
  const [attempt, setAttempt] = useState(0);
  const L = LOOK[variant];

  useEffect(() => {
    // ⚠️ Une réponse arrivée après un changement de nature ne remplace pas la liste de la nouvelle.
    let live = true;
    setState({ status: 'loading' });
    api.quickStudioSources(kind).then(
      (r) => { if (live) setState({ status: 'ready', rows: r.rows }); },
      (e: unknown) => { if (live) setState({ status: 'error', message: e instanceof Error ? e.message : String(e) }); },
    );
    return () => { live = false; };
  }, [kind, attempt]);

  const hits = useMemo(() => {
    if (state.status !== 'ready') return [];
    const needle = fold(query.trim());
    return needle ? state.rows.filter((s) => fold(s.label).includes(needle)) : state.rows;
  }, [state, query]);
  const shown = hits.slice(0, SHOWN_MAX);
  // ⚠️ « PARMI 15 VALEURS », PAS « 15 FAMILLES » comme la maquette : le mot du champ est celui du magasin, qui peut l'avoir
  //    renommé, et le vocabulaire ne donne pas de pluriel.
  const placeholder = state.status === 'ready' ? t('Rechercher parmi {n} valeur…', { n: state.rows.length }) : t('Rechercher…');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...(variant === 'window' ? { flex: 1 } : null) }}>
      <div style={L.head}>
        <input
          autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder} aria-label={t('Rechercher…')} className="oz-qs-source-search"
          style={{ display: 'block', width: '100%', boxSizing: 'border-box', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', color: 'var(--ink-900)', outline: 'none', ...L.input }}
        />
      </div>
      <div style={L.list}>
        {state.status === 'loading' && <Note>{t('Lecture du référentiel…')}</Note>}
        {state.status === 'error' && (
          <div style={{ padding: '10px', fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', lineHeight: 1.45 }}>
            {t('La liste ne se lit pas : {motif}', { motif: state.message })}
            <button type="button" onClick={() => setAttempt((n) => n + 1)}
              style={{ display: 'block', marginTop: 6, padding: 0, border: 'none', background: 'transparent', color: 'inherit', textDecoration: 'underline', fontWeight: 700 }}>
              {t('Réessayer')}
            </button>
          </div>
        )}
        {state.status === 'ready' && state.rows.length === 0 && (
          <Note>{t('Rien à proposer : aucune valeur de « {champ} » ne porte d’article vendable.', { champ: sourceWord(kind) })}</Note>
        )}
        {state.status === 'ready' && state.rows.length > 0 && hits.length === 0 && <Note>{t('Aucun résultat')}</Note>}
        {shown.map((s) => {
          const on = s.id === current;
          return (
            <button key={s.id} type="button" onClick={() => onPick(s)} className="oz-qs-add-kind"
              style={{ display: 'flex', alignItems: 'center', width: '100%', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--teal-100)' : 'transparent', textAlign: 'left', ...L.row }}>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: on ? 'var(--teal-800)' : 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
              <span style={{ flexShrink: 0, fontSize: L.count, color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{t('{n} art.', { n: s.count })}</span>
              {on && <Icon name="check" size={L.check} stroke={3} color="var(--teal-600)" style={{ flexShrink: 0 }} />}
            </button>
          );
        })}
        {hits.length > SHOWN_MAX && <Note>{t('{n} premiers affichés — affinez la recherche', { n: SHOWN_MAX })}</Note>}
      </div>
    </div>
  );
}

/**
 * LA FENÊTRE « CHOISIR » D'UNE SOURCE QU'ON AJOUTE (maquette `srcModal`) — la tuile ne naît qu'au choix fait : fermer la
 * fenêtre n'ajoute rien.
 * ⚠️ LA MAQUETTE POSE D'ABORD UNE TUILE PAR DÉFAUT, PUIS OUVRE LA FENÊTRE : une famille choisie au hasard resterait à
 *    l'écran si l'on fermait sans choisir, et une source sans valeur réelle serait refusée à la publication.
 * ⚠️ LE TITRE NOMME LE CHAMP DANS LE MOT DU MAGASIN (« Choisir : Millésime »), pas « Choisir la famille » : un mot
 *    renommé n'a pas forcément le genre de celui de la plateforme.
 */
export function QuickStudioSourceChooser({ kind, onPick, onClose }: {
  kind: QuickSourceKind;
  onPick: (source: StudioSource) => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  return (
    <ModalBackdrop onBackdrop={onClose}>
      <div ref={panelRef} role="dialog" aria-modal aria-labelledby={titleId} tabIndex={-1}
        style={{ position: 'relative', width: 420, maxWidth: '96vw', maxHeight: '78vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: '0 28px 70px -12px rgba(0,0,0,.45)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span id={titleId} style={{ display: 'block', fontSize: 15.5, fontWeight: 700, color: 'var(--ink-900)' }}>{t('Choisir : {champ}', { champ: sourceWord(kind) })}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45 }}>{t('Le contenu de la tuile est lu en direct dans le catalogue.')}</span>
          </span>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            style={{ width: 30, height: 30, flexShrink: 0, border: 'none', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <Icon name="x" size={14} stroke={2.4} />
          </button>
        </div>
        <QuickStudioSourceList kind={kind} current={null} onPick={onPick} variant="window" />
      </div>
    </ModalBackdrop>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '14px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)', lineHeight: 1.45 }}>{children}</div>;
}
